/**
 * @module agent/options/agentOptionsUtils
 *
 * Pure helpers for the reusable agent numbered-options bar (TD-2362).
 *
 * The agent emits a structured options payload (parsed from an
 * `[AGENT_OPTIONS:...]` tag) and the FE renders it as a numbered-options bar.
 * The user's choice is sent back to the agent as an ordinary chat message —
 * by number or free text. Nothing side-effecting runs on selection; a
 * spend/side-effecting action still opens its own confirm step.
 *
 * All functions are pure (no React, no DOM) so they can be unit-tested and
 * reused by the tag parser / simulation harness.
 */

/**
 * @typedef {Object} AgentOptionItem
 * @property {string} id - Stable identifier for the option.
 * @property {string} label - Human-readable headline (also the default reply).
 * @property {string} [replyValue] - Explicit reply text to send (defaults to label).
 * @property {string} [description] - One-line description of what the option does.
 * @property {number} [cost] - Credit cost, rendered as a cost pill.
 * @property {string} [costLabel] - Overrides the derived "N credits" pill text.
 * @property {boolean} [free] - Render a "free" pill instead of a cost.
 * @property {string} [badge] - Neutral pill text (e.g. a count).
 */

/**
 * @typedef {Object} AgentOptionsPayload
 * @property {string} [id] - Options-turn id (UUID from the tag).
 * @property {'reveal_choices'|'roles'|'next_steps'|string} [kind] - Drives the tag icon/label.
 * @property {string} [label] - Short tag label (e.g. "Reveal options").
 * @property {string} [headline] - The one-line question.
 * @property {string} [helper] - Optional sub-note / run-bar hint.
 * @property {boolean} [multiSelect] - Toggle several then run, vs. pick one.
 * @property {boolean} [allowCustom] - Show the always-last custom free-text option.
 * @property {AgentOptionItem[]} [items] - Flat option list.
 * @property {{label:string, items:AgentOptionItem[]}[]} [groups] - Grouped option list.
 * @property {string} [submitLabel] - Multi-select run-button label.
 * @property {string} [inputPlaceholder] - Free-text input placeholder.
 * @property {string} [customLabel] - Custom option headline.
 * @property {string} [customDescription] - Custom option description.
 */

/**
 * Flatten grouped or flat option items into a single ordered array.
 * Group order is preserved; used for continuous numbering and number replies.
 *
 * @param {AgentOptionsPayload|null|undefined} options
 * @returns {AgentOptionItem[]}
 */
export function flattenOptionItems(options) {
  if (!options) return [];
  if (Array.isArray(options.groups) && options.groups.length > 0) {
    return options.groups.flatMap((group) => (Array.isArray(group.items) ? group.items : []));
  }
  return Array.isArray(options.items) ? options.items : [];
}

/**
 * Reply text for a single option (sent verbatim to the agent).
 * @param {AgentOptionItem|null|undefined} item
 * @returns {string}
 */
function itemReply(item) {
  return String(item?.replyValue ?? item?.label ?? '').trim();
}

/**
 * Build the canonical reply string from a selection.
 * - Single-select: the chosen item's reply, or the free-text custom input.
 * - Multi-select: comma-joined selected labels, with any custom text appended.
 * Returns '' when there is nothing to send (caller should no-op).
 *
 * @param {Object} params
 * @param {AgentOptionItem[]} [params.selectedItems] - Chosen option(s).
 * @param {string} [params.customText] - Free-text custom input.
 * @returns {string}
 */
export function buildOptionReply({ selectedItems = [], customText = '' } = {}) {
  const custom = String(customText || '').trim();
  const labels = selectedItems.map(itemReply).filter(Boolean);
  if (labels.length === 0) return custom;
  if (custom) return [...labels, custom].join(', ');
  return labels.join(', ');
}

/**
 * If `text` is a bare option number (1-based) within range, return that item;
 * otherwise null. Lets the user answer a numbered list by typing "2".
 *
 * @param {AgentOptionsPayload} options
 * @param {string} text
 * @returns {AgentOptionItem|null}
 */
export function resolveNumberedInput(options, text) {
  const trimmed = String(text || '').trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const index = Number.parseInt(trimmed, 10) - 1;
  const items = flattenOptionItems(options);
  return index >= 0 && index < items.length ? items[index] : null;
}
